import { useState, useMemo } from "react";
import {
  Caso,
  Holding,
  Propiedad,
  Cliente,
  Usuario,
  LugarPresentacion,
  UsuarioSelector,
  CategoriaPresentacion,
  EstadoCaso,
} from "../../types";

interface CasoFormProps {
  caso?: Caso | null;
  holdings: Holding[];
  propiedades: Propiedad[];
  clientes: Cliente[];
  usuarios: Usuario[];
  defaultHoldingId?: number;
  onSubmit: (data: Omit<Caso, "id" | "fechaCreacion" | "fechaActualizacion">) => void;
  onCancel: () => void;
}

export function CasoForm({
  caso,
  holdings,
  propiedades,
  clientes,
  usuarios,
  defaultHoldingId,
  onSubmit,
  onCancel,
}: CasoFormProps) {
  const [holdingId, setHoldingId] = useState(caso?.holdingId || defaultHoldingId || 0);
  const [holdingSearch, setHoldingSearch] = useState("");
  const [showHoldingDropdown, setShowHoldingDropdown] = useState(false);
  const [titulo, setTitulo] = useState(caso?.titulo || "");
  const [lugarPresentacion, setLugarPresentacion] = useState<LugarPresentacion>(
    caso?.lugarPresentacion || "SII"
  );
  const [usuario, setUsuario] = useState<UsuarioSelector>(caso?.usuario || "Oficina");
  const [categoriaPresentacion, setCategoriaPresentacion] = useState<CategoriaPresentacion>(
    caso?.categoriaPresentacion || "actualizacion_modificacion"
  );
  const [tipoPresentacion, setTipoPresentacion] = useState(
    caso?.tipoPresentacion || ""
  );
  const [estado, setEstado] = useState<EstadoCaso>(caso?.estado || "rol_listo");
  const [ejecutivoId, setEjecutivoId] = useState(caso?.ejecutivoId || 0);
  const [abogadoId, setAbogadoId] = useState(caso?.abogadoId || 0);
  const [arquitectoId, setArquitectoId] = useState(caso?.arquitectoId || 0);
  const [rebajaEsperada, setRebajaEsperada] = useState(caso?.rebajaEsperada?.toString() || "");
  const [honorario, setHonorario] = useState(caso?.honorario?.toString() || "");
  const [selectedPropiedadIds, setSelectedPropiedadIds] = useState<number[]>([]);

  const ejecutivos = usuarios.filter((u) => u.rol === "ejecutivo" || u.rol === "admin");
  const abogados = usuarios.filter((u) => u.rol === "abogado" || u.rol === "admin");
  const arquitectos = usuarios.filter((u) => u.rol === "arquitecto" || u.rol === "admin");

  const selectedHolding = holdings.find((h) => h.id === holdingId);
  const holdingClientes = clientes.filter((c) => c.holdingId === holdingId);
  const holdingClienteIds = holdingClientes.map((c) => c.id);
  const holdingPropiedades = propiedades.filter((p) => holdingClienteIds.includes(p.clienteId));

  const isHoldingLocked = !!caso || !!defaultHoldingId;

  const togglePropiedad = (id: number) => {
    setSelectedPropiedadIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const filteredHoldings = useMemo(() => {
    if (!holdingSearch) return holdings;
    const search = holdingSearch.toLowerCase();
    return holdings.filter((h) => h.nombre.toLowerCase().includes(search));
  }, [holdings, holdingSearch]);

  const handleHoldingSelect = (holding: Holding) => {
    setHoldingId(holding.id);
    setHoldingSearch("");
    setShowHoldingDropdown(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      holdingId,
      titulo: titulo.trim(),
      lugarPresentacion,
      usuario,
      categoriaPresentacion,
      tipoPresentacion,
      estado,
      ejecutivoId,
      abogadoId: abogadoId || undefined,
      arquitectoId: arquitectoId || undefined,
      rebajaEsperada: rebajaEsperada ? Number(rebajaEsperada) : undefined,
      honorario: honorario ? Number(honorario) : undefined,
    });
  };

  const isValid = holdingId > 0 && titulo.trim().length > 0 && ejecutivoId > 0 && selectedPropiedadIds.length > 0 && tipoPresentacion.length > 0;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Holding Selector con busqueda */}
      <div className="relative">
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Holding *
        </label>
        <div
          className={`w-full px-3 py-2 border border-slate-200 rounded-lg text-sm cursor-pointer bg-white ${
            isHoldingLocked ? "bg-slate-100 cursor-not-allowed" : ""
          }`}
          onClick={() => !isHoldingLocked && setShowHoldingDropdown(!showHoldingDropdown)}
        >
          {selectedHolding ? (
            <span className="text-slate-800">{selectedHolding.nombre}</span>
          ) : (
            <span className="text-slate-400">Seleccionar Holding...</span>
          )}
        </div>

        {showHoldingDropdown && !isHoldingLocked && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-hidden">
            <div className="p-2 border-b border-slate-200">
              <input
                type="text"
                value={holdingSearch}
                onChange={(e) => setHoldingSearch(e.target.value)}
                placeholder="Buscar por nombre..."
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
            </div>
            <div className="max-h-48 overflow-y-auto">
              {filteredHoldings.map((holding) => (
                <div
                  key={holding.id}
                  onClick={() => handleHoldingSelect(holding)}
                  className={`px-3 py-2 cursor-pointer hover:bg-slate-100 ${
                    holding.id === holdingId ? "bg-blue-50 text-blue-700" : ""
                  }`}
                >
                  <div className="font-medium">{holding.nombre}</div>
                  <div className="text-xs text-slate-500">{holding.tipo}</div>
                </div>
              ))}
              {filteredHoldings.length === 0 && (
                <div className="px-3 py-2 text-slate-500 text-sm">
                  No se encontraron holdings
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Propiedad picklist grouped by Cliente */}
      {selectedHolding && holdingPropiedades.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Propiedades (Comuna + ROL) *
          </label>
          <div className="border border-slate-200 rounded-lg max-h-48 overflow-y-auto">
            {holdingClientes.map((c) => {
              const clienteProps = holdingPropiedades.filter((p) => p.clienteId === c.id);
              if (clienteProps.length === 0) return null;
              return (
                <div key={c.id}>
                  <div className="px-3 py-1.5 bg-slate-50 text-xs font-medium text-slate-500 border-b border-slate-100">
                    {c.nombre} ({c.rut})
                  </div>
                  {clienteProps.map((p) => (
                    <label
                      key={p.id}
                      className={`flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-slate-50 border-b border-slate-100 last:border-b-0 ${
                        selectedPropiedadIds.includes(p.id) ? "bg-blue-50" : ""
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedPropiedadIds.includes(p.id)}
                        onChange={() => togglePropiedad(p.id)}
                        className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-slate-700">
                        {p.comuna} - ROL {p.rol}
                        {p.tipoTenencia === "leasing" && (
                          <span className="ml-1 text-xs text-yellow-600">(Leasing)</span>
                        )}
                      </span>
                    </label>
                  ))}
                </div>
              );
            })}
          </div>
          {selectedPropiedadIds.length === 0 && (
            <p className="text-xs text-amber-600 mt-1">Seleccione al menos una propiedad</p>
          )}
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Nombre del Caso *
        </label>
        <input
          type="text"
          value={titulo}
          onChange={(e) => setTitulo(e.target.value)}
          placeholder="Ej: AUP Propiedad Las Condes"
          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Lugar Presentacion
          </label>
          <select
            value={lugarPresentacion}
            onChange={(e) => setLugarPresentacion(e.target.value as LugarPresentacion)}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="SII">SII</option>
            <option value="Municipalidad">Municipalidad</option>
            <option value="TTA">TTA</option>
            <option value="Corte_Apelaciones">Corte de Apelaciones</option>
            <option value="Otro">Otro</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Usuario SII
          </label>
          <select
            value={usuario}
            onChange={(e) => setUsuario(e.target.value as UsuarioSelector)}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="Oficina">Oficina</option>
            <option value="Cliente">Cliente</option>
            <option value="Cristobal">Cristobal</option>
          </select>
        </div>
      </div>

      {/* Tipo de Presentacion - 2 niveles */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Tipo de Presentacion *
        </label>
        <select
          value={categoriaPresentacion}
          onChange={(e) => {
            setCategoriaPresentacion(e.target.value as CategoriaPresentacion);
            setTipoPresentacion("");
          }}
          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="actualizacion_modificacion">Actualizacion y/o Modificacion del Bien Raiz</option>
          <option value="rebajas_beneficios">Rebajas, beneficios y exenciones de contribuciones</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Sub-tipo *
        </label>
        <select
          value={tipoPresentacion}
          onChange={(e) => setTipoPresentacion(e.target.value)}
          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Seleccionar...</option>
          {categoriaPresentacion === "actualizacion_modificacion" ? (
            <>
              <option value="Revisión sobretasa Sitios No Edificados">Revision sobretasa Sitios No Edificados</option>
              <option value="Actualización de la ubicación en el mapa del bien raíz">Actualizacion de la ubicacion en el mapa del bien raiz</option>
              <option value="Cambio de nombre y/o RUT del propietario">Cambio de nombre y/o RUT del propietario</option>
              <option value="Informar Demoliciones y Siniestros de Bienes Raíces">Informar Demoliciones y Siniestros de Bienes Raices</option>
              <option value="Cambio de Dirección del Bien Raíz">Cambio de Direccion del Bien Raiz</option>
              <option value="Modificación y/o revisión de Avalúo de Construcción">Modificacion y/o revision de Avaluo de Construccion</option>
              <option value="Modificación y/o revisión de Avalúo de Terreno">Modificacion y/o revision de Avaluo de Terreno</option>
              <option value="Modificación y/o revisión de Destino del Bien Raíz">Modificacion y/o revision de Destino del Bien Raiz</option>
              <option value="Modificación y/o revisión de la Vigencia de la Tasación Fiscal">Modificacion y/o revision de la Vigencia de la Tasacion Fiscal</option>
              <option value="Revisión de la Serie a la cual pertenece: Agrícola o No Agrícola">Revision de la Serie a la cual pertenece: Agricola o No Agricola</option>
              <option value="Tasación de Obra nueva y/o Ampliación">Tasacion de Obra nueva y/o Ampliacion</option>
            </>
          ) : (
            <>
              <option value="Beneficio DFL-2: Solicitud, Revisión, o Rechazo">Beneficio DFL-2: Solicitud, Revision, o Rechazo</option>
              <option value="Rebaja Adulto Mayor: Solicitud, Revisión, o Rechazo">Rebaja Adulto Mayor: Solicitud, Revision, o Rechazo</option>
              <option value="Rebaja de avalúo o contribuciones de inmuebles ubicados en zonas afectadas por catástrofes">Rebaja de avaluo o contribuciones en zonas afectadas por catastrofes</option>
              <option value="Rebaja de Contribuciones a Predios Ocupados Ilegalmente">Rebaja de Contribuciones a Predios Ocupados Ilegalmente</option>
              <option value="Revisión y/o Solicitud de Exención de Impuesto Territorial">Revision y/o Solicitud de Exencion de Impuesto Territorial</option>
            </>
          )}
        </select>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Estado
          </label>
          <select
            value={estado}
            onChange={(e) => setEstado(e.target.value as EstadoCaso)}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="rol_listo">ROL Listo</option>
            <option value="en_preparacion">En Preparacion</option>
            <option value="confirmacion">Confirmacion</option>
            <option value="listo_para_presentar">Listo para Presentar</option>
            <option value="solicitud_administrativa">Solicitud Administrativa</option>
            <option value="juicio">Juicio</option>
            <option value="tesoreria">Tesoreria</option>
            <option value="recursos_administrativos">Recursos Administrativos</option>
            <option value="cobro_facturacion">Cobro/Facturacion</option>
            <option value="desistido">Desistido</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Ejecutivo al Mando *
          </label>
          <select
            value={ejecutivoId}
            onChange={(e) => setEjecutivoId(Number(e.target.value))}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value={0}>Seleccionar...</option>
            {ejecutivos.map((u) => (
              <option key={u.id} value={u.id}>
                {u.nombre}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Abogado
          </label>
          <select
            value={abogadoId}
            onChange={(e) => setAbogadoId(Number(e.target.value))}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value={0}>Sin asignar</option>
            {abogados.map((u) => (
              <option key={u.id} value={u.id}>
                {u.nombre}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Arquitecto
          </label>
          <select
            value={arquitectoId}
            onChange={(e) => setArquitectoId(Number(e.target.value))}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value={0}>Sin asignar</option>
            {arquitectos.map((u) => (
              <option key={u.id} value={u.id}>
                {u.nombre}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="border-t border-slate-200 pt-4">
        <h4 className="text-sm font-semibold text-slate-700 mb-3">
          Valores Estimados
        </h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Rebaja Esperada ($)
            </label>
            <input
              type="number"
              value={rebajaEsperada}
              onChange={(e) => setRebajaEsperada(e.target.value)}
              placeholder="10000000"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Honorario ($)
            </label>
            <input
              type="number"
              value={honorario}
              onChange={(e) => setHonorario(e.target.value)}
              placeholder="2500000"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={!isValid}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:bg-slate-300 disabled:cursor-not-allowed"
        >
          {caso ? "Guardar Cambios" : "Crear Caso"}
        </button>
      </div>
    </form>
  );
}
